import { useEffect } from "react";

import {
  CustomSummaryResultFragment,
  SaveCustomSummaryResultMutationVariables,
  SummaryTemplateResult,
} from "@/graphql/generated/graphql";
import { useCall } from "@/hooks/useCall/useCall";
import { useCurrentUser } from "@/hooks/useCurrentUser/useCurrentUser";
import { useGetLicenses } from "@/hooks/useGetLicenses/useGetLicenses";
import { useSaveCustomSummaryResult } from "@/hooks/useSaveCustomSummaryResult";
import { useTrack } from "@/hooks/useTrack/useTrack";
import { CustomSummaryResultsFormField } from "./CustomSummaryResultsFormField";

type SummaryTemplateEntry =
  CustomSummaryResultFragment["customSummary"]["templates"][number];

type CustomSummaryResultsFormProps = {
  customSummaryResultId: string;
  templateResults: SummaryTemplateResult[];
  templates: SummaryTemplateEntry[];
};

export function CustomSummaryResultsForm({
  customSummaryResultId,
  templateResults,
  templates,
}: CustomSummaryResultsFormProps) {
  const { hasLicense } = useGetLicenses();
  const canUpdate = hasLicense("ai_assist_pro");
  const { saveCustomSummaryResult } = useSaveCustomSummaryResult();
  const { track } = useTrack();
  const { call } = useCall();
  const { currentUser } = useCurrentUser();

  // Track custom summary viewed
  useEffect(() => {
    if (call?.id && currentUser?.ID) {
      track("custom_summary_viewed", {
        call_id: String(call.id),
        agent_id: String(currentUser.ID),
        view_location: "assets-page",
      });
    }
  }, [call?.id, currentUser?.ID, track]);

  const handleUpdate = async (
    templateResult: SummaryTemplateResult,
    newValue: string
  ) => {
    if (!canUpdate) {
      return;
    }

    // Check that value has changed
    if (templateResult.content === newValue) {
      return;
    }

    // Save the updated template result
    const input: SaveCustomSummaryResultMutationVariables["input"] = {
      id: customSummaryResultId,
      templateResults: [
        {
          id: templateResult.id,
          content: newValue,
        },
      ],
    };

    await saveCustomSummaryResult({ input });

    // Track the custom summary edit
    if (call?.id && currentUser?.ID) {
      track("custom_summary_edited", {
        call_id: String(call.id),
        agent_id: String(currentUser.ID),
        edit_location: "assets-page",
      });
    }
  };

  return (
    <div className="flex w-full flex-col gap-4">
      {templates.map((templateItem) => {
        const template = templateItem.template;
        const templateId = template?.id;
        const templateResult = templateResults.find(
          (templateResult) =>
            templateResult.summaryTemplateId.toString() === templateId
        );

        if (!templateResult) {
          return null;
        }

        return (
          <CustomSummaryResultsFormField
            key={templateResult.id}
            templateResult={templateResult}
            template={template}
            onUpdate={(newValue) => {
              handleUpdate(templateResult, newValue);
            }}
            readOnly={!canUpdate}
          />
        );
      })}
    </div>
  );
}
